/**
 * Temporary developer tool: edit every color variable of every theme live,
 * rename / add / delete themes, and persist the edits on Save.
 *
 * color overrides are stored per theme in localStorage and re-applied at
 * startup via a single injected <style> element whose `:root[data-theme="…"]`
 * rules win over the built-in theme blocks (same specificity, later in the
 * cascade). Custom (added) themes have no built-in block, so their override
 * carries the *full* variable set, making them self-contained.
 *
 * A small "theme registry" (renames, deletions and custom themes) is persisted
 * separately so both this editor and the Settings picker show the same list.
 */

import { el, on } from "./dom.js";

const STORAGE_KEY = "goodsudoku:themeOverrides";
const META_KEY = "goodsudoku:themeMeta";
const STYLE_ID = "theme-overrides";

/** Every color custom-property a theme defines. */
export const THEME_VARS: string[] = [
  "--primary", "--primary-deep", "--on-primary",
  "--bg", "--surface", "--surface-2",
  "--board-line", "--board-line-strong",
  "--ink", "--ink-soft", "--given", "--entered", "--note",
  "--selection", "--peer", "--samedigit", "--hintlight",
  "--error", "--error-bg",
  "--pad-btn", "--pad-btn-ink",
  "--note-pencil", "--note-accent", "--note-accent-bg", "--note-cross", "--note-cross-bg",
  "--easy", "--medium", "--hard", "--expert", "--insane",
];

export interface ThemeOpt {
  id: string;
  name: string;
  bg: string;
  fg: string;
  accent: string;
}

type Overrides = Record<string, Record<string, string>>; // theme -> var -> value

interface ThemeMeta {
  renames: Record<string, string>; // built-in id -> custom display name
  deleted: string[]; // hidden built-in ids
  custom: ThemeOpt[]; // added themes (rendered from their override block)
  order: string[]; // explicit display order (ids); unlisted ids keep default order
}

// ---------------------------------------------------------------------------
// Persistence helpers
// ---------------------------------------------------------------------------

function loadSaved(): Overrides {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") as Overrides;
  } catch {
    return {};
  }
}

function loadMeta(): ThemeMeta {
  try {
    const m = JSON.parse(localStorage.getItem(META_KEY) || "{}") as Partial<ThemeMeta>;
    return {
      renames: m.renames || {},
      deleted: m.deleted || [],
      custom: m.custom || [],
      order: m.order || [],
    };
  } catch {
    return { renames: {}, deleted: [], custom: [], order: [] };
  }
}

function saveMeta(m: ThemeMeta): void {
  localStorage.setItem(META_KEY, JSON.stringify(m));
}

/** Persist (or remove) a single theme's override block without flushing other
 *  in-flight edits, keeps a freshly added/deleted theme valid before Save. */
function persistOverride(id: string, vars: Record<string, string> | null): void {
  const saved = loadSaved();
  if (vars) saved[id] = vars;
  else delete saved[id];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
}

function buildCss(ov: Overrides): string {
  let css = "";
  for (const theme of Object.keys(ov)) {
    const decls = Object.entries(ov[theme] || {})
      .map(([k, v]) => `${k}:${v};`)
      .join("");
    if (decls) css += `:root[data-theme="${theme}"]{${decls}}`;
  }
  return css;
}

function injectCss(css: string): void {
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = css;
}

/** Apply saved theme overrides, call once at startup. */
export function applyThemeOverrides(): void {
  injectCss(buildCss(loadSaved()));
}

/** The effective theme list: built-ins (minus deletions, with renames) then
 *  any custom themes. Shared by the Settings picker and this editor. */
export function effectiveThemes(builtins: ThemeOpt[]): ThemeOpt[] {
  const meta = loadMeta();
  const deleted = new Set(meta.deleted);
  const list: ThemeOpt[] = [];
  for (const b of builtins) {
    if (deleted.has(b.id)) continue;
    list.push({ ...b, name: meta.renames[b.id] ?? b.name });
  }
  for (const c of meta.custom) list.push({ ...c });
  // Apply the saved display order; ids missing from it keep their default order.
  const pos = new Map(meta.order.map((id, i) => [id, i]));
  return list
    .map((t, i) => ({ t, i }))
    .sort((a, b) => {
      const pa = pos.has(a.t.id) ? pos.get(a.t.id)! : Infinity;
      const pb = pos.has(b.t.id) ? pos.get(b.t.id)! : Infinity;
      return pa - pb || a.i - b.i;
    })
    .map((x) => x.t);
}

function expandHex(v: string): string {
  return /^#[0-9a-f]{3}$/i.test(v)
    ? "#" + v.slice(1).split("").map((c) => c + c).join("")
    : v;
}

const hex2 = (n: number): string =>
  Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");

function rgbToHex(r: number, g: number, b: number): string {
  return "#" + hex2(r) + hex2(g) + hex2(b);
}

function hexToRgb(hex: string): [number, number, number] {
  const h = expandHex(hex).slice(1);
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** Parse a hex / rgb / rgba color into a picker-friendly hex plus optional
 *  alpha (null when the value carries no alpha channel). Returns null for
 *  values the color input can't represent. */
function parseColor(v: string): { hex: string; alpha: number | null } | null {
  const s = v.trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) return { hex: expandHex(s), alpha: null };
  const m = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i);
  if (m) {
    const a = m[4] === undefined ? null : parseFloat(m[4]);
    return { hex: rgbToHex(parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3])), alpha: a };
  }
  return null;
}

/** Re-assemble a color from a hex picker value and an optional alpha. */
function composeColor(hex: string, alpha: number | null): string {
  if (alpha === null) return hex;
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

export class DevColorEditor {
  private working: Overrides = loadSaved();
  private rowsEl!: HTMLElement;
  private sel!: HTMLSelectElement;

  constructor(
    private container: HTMLElement,
    private builtins: ThemeOpt[],
    private onThemeChange: (id: string) => void,
    private onThemesChange: () => void,
  ) {
    this.build();
  }

  private activeTheme(): string {
    return document.documentElement.dataset.theme || "classic";
  }

  private isBuiltin(id: string): boolean {
    return this.builtins.some((b) => b.id === id);
  }

  private themeName(id: string): string {
    return effectiveThemes(this.builtins).find((t) => t.id === id)?.name ?? id;
  }

  private apply(): void {
    injectCss(buildCss(this.working));
  }

  private populateSelect(): void {
    this.sel.innerHTML = "";
    for (const t of effectiveThemes(this.builtins)) {
      const o = el("option") as HTMLOptionElement;
      o.value = t.id;
      o.textContent = t.name;
      this.sel.appendChild(o);
    }
    this.sel.value = this.activeTheme();
  }

  private build(): void {
    this.container.innerHTML = "";
    const panel = el("div", "dev-panel");

    const head = el("div", "dev-head");
    head.appendChild(el("span", "dev-title", "Theme colors, dev tool"));
    this.sel = el("select", "dev-theme") as HTMLSelectElement;
    on(this.sel, "change", () => {
      this.onThemeChange(this.sel.value);
      this.syncMetaRow();
      this.renderRows();
    });
    head.appendChild(this.sel);
    const addBtn = el("button", "dev-mini", "+ New");
    on(addBtn, "click", () => this.addTheme());
    head.appendChild(addBtn);
    panel.appendChild(head);

    // Rename, reorder and delete the selected theme.
    const metaRow = el("div", "dev-meta");
    const nameInput = el("input", "dev-name") as HTMLInputElement;
    nameInput.type = "text";
    nameInput.spellcheck = false;
    nameInput.value = this.themeName(this.activeTheme());
    on(nameInput, "input", () => this.renameTheme(nameInput.value));
    const upBtn = el("button", "dev-mini", "↑");
    upBtn.title = "Move theme up";
    on(upBtn, "click", () => this.moveTheme(-1));
    const downBtn = el("button", "dev-mini", "↓");
    downBtn.title = "Move theme down";
    on(downBtn, "click", () => this.moveTheme(1));
    const delBtn = el("button", "dev-mini danger", "Delete");
    on(delBtn, "click", () => this.deleteTheme());
    metaRow.append(nameInput, upBtn, downBtn, delBtn);
    panel.appendChild(metaRow);

    this.rowsEl = el("div", "dev-rows");
    panel.appendChild(this.rowsEl);

    const actions = el("div", "dev-actions");
    const reset = el("button", "btn");
    reset.textContent = "Reset theme";
    on(reset, "click", () => {
      delete this.working[this.activeTheme()];
      this.apply();
      this.renderRows();
    });
    const save = el("button", "btn primary");
    save.textContent = "Save";
    on(save, "click", () => {
      this.refreshCustomSwatch();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.working));
      this.onThemesChange();
      save.textContent = "Saved";
      window.setTimeout(() => (save.textContent = "Save"), 1200);
    });
    actions.append(reset, save);
    panel.appendChild(actions);

    this.container.appendChild(panel);
    this.populateSelect();
    this.syncMetaRow();
    this.renderRows();
  }

  /** Keep the rename field and delete button in step with the active theme. */
  private syncMetaRow(): void {
    const name = this.container.querySelector(".dev-name") as HTMLInputElement | null;
    const del = this.container.querySelector(".dev-mini.danger") as HTMLButtonElement | null;
    const active = this.activeTheme();
    if (name) name.value = this.themeName(active);
    if (del) del.disabled = active === "classic"; // keep a guaranteed default
  }

  /** Call when the active theme changed elsewhere (e.g. Settings). */
  refresh(): void {
    if (this.sel) this.sel.value = this.activeTheme();
    this.syncMetaRow();
    this.renderRows();
  }

  // ---- structural operations --------------------------------------------

  private renameTheme(name: string): void {
    const id = this.activeTheme();
    const meta = loadMeta();
    const custom = meta.custom.find((c) => c.id === id);
    if (custom) custom.name = name;
    else if (this.isBuiltin(id)) meta.renames[id] = name;
    else return;
    saveMeta(meta);
    const opt = Array.from(this.sel.options).find((o) => o.value === id);
    if (opt) opt.textContent = name;
    this.onThemesChange();
  }

  private moveTheme(dir: -1 | 1): void {
    const id = this.activeTheme();
    const ids = effectiveThemes(this.builtins).map((t) => t.id);
    const idx = ids.indexOf(id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    const meta = loadMeta();
    meta.order = ids;
    saveMeta(meta);
    this.populateSelect();
    this.onThemesChange();
  }

  private addTheme(): void {
    const base = this.activeTheme();
    const cs = getComputedStyle(document.documentElement);
    const vars: Record<string, string> = {};
    for (const v of THEME_VARS)
      vars[v] = (this.working[base]?.[v] ?? cs.getPropertyValue(v).trim()) || "#888888";

    const meta = loadMeta();
    const n = meta.custom.length + 1;
    const id = "custom-" + Date.now().toString(36);
    meta.custom.push({
      id,
      name: `Custom ${n}`,
      bg: vars["--bg"] || "#eee9e7",
      fg: vars["--ink"] || "#161718",
      accent: vars["--primary"] || "#888888",
    });
    saveMeta(meta);

    this.working[id] = vars;
    persistOverride(id, vars); // valid immediately, before Save
    this.apply();
    this.onThemesChange(); // refresh pickers with the new id known
    this.onThemeChange(id); // switch to it
    this.build();
  }

  private deleteTheme(): void {
    const id = this.activeTheme();
    if (id === "classic") return;
    const meta = loadMeta();
    if (meta.custom.some((c) => c.id === id)) {
      meta.custom = meta.custom.filter((c) => c.id !== id);
      persistOverride(id, null);
      delete this.working[id];
    } else if (this.isBuiltin(id) && !meta.deleted.includes(id)) {
      meta.deleted.push(id);
    }
    saveMeta(meta);
    this.apply();
    this.onThemeChange("classic"); // move off the removed theme
    this.onThemesChange();
    this.build();
  }

  /** Refresh a custom theme's Settings swatch from its current key colors. */
  private refreshCustomSwatch(): void {
    const id = this.activeTheme();
    const meta = loadMeta();
    const custom = meta.custom.find((c) => c.id === id);
    if (!custom) return;
    custom.bg = this.current("--bg") || custom.bg;
    custom.fg = this.current("--ink") || custom.fg;
    custom.accent = this.current("--primary") || custom.accent;
    saveMeta(meta);
  }

  // ---- color rows -------------------------------------------------------

  private current(varName: string): string {
    const ov = this.working[this.activeTheme()]?.[varName];
    if (ov) return ov;
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  }

  private setVar(varName: string, value: string): void {
    const theme = this.activeTheme();
    (this.working[theme] ??= {})[varName] = value;
    this.apply();
  }

  private renderRows(): void {
    this.rowsEl.innerHTML = "";
    for (const v of THEME_VARS) {
      const value = this.current(v);
      const parsed = parseColor(value);
      const row = el("div", "dev-row");

      const picker = el("input", "dev-pick") as HTMLInputElement;
      picker.type = "color";
      picker.value = parsed ? parsed.hex : "#888888";
      if (!parsed) picker.style.visibility = "hidden";

      const label = el("span", "dev-var");
      label.textContent = v.replace(/^--/, "");

      const text = el("input", "dev-text") as HTMLInputElement;
      text.type = "text";
      text.value = value;
      text.spellcheck = false;

      // For rgba colors, an alpha box lets the color picker drive the RGB
      // channels while keeping (and tweaking) the transparency.
      let alpha: number | null = parsed ? parsed.alpha : null;
      const alphaEl = el("input", "dev-alpha") as HTMLInputElement;
      alphaEl.type = "number";
      alphaEl.min = "0";
      alphaEl.max = "1";
      alphaEl.step = "0.01";
      alphaEl.title = "Alpha (0–1)";
      if (alpha === null) alphaEl.style.visibility = "hidden";
      else alphaEl.value = String(alpha);

      on(text, "input", () => {
        const val = text.value.trim();
        this.setVar(v, val);
        const p = parseColor(val);
        if (p) {
          picker.value = p.hex;
          picker.style.visibility = "visible";
          alpha = p.alpha;
          if (p.alpha === null) {
            alphaEl.style.visibility = "hidden";
          } else {
            alphaEl.style.visibility = "visible";
            alphaEl.value = String(p.alpha);
          }
        }
      });
      on(picker, "input", () => {
        const out = composeColor(picker.value, alpha);
        text.value = out;
        this.setVar(v, out);
      });
      on(alphaEl, "input", () => {
        const a = parseFloat(alphaEl.value);
        alpha = isNaN(a) ? null : Math.max(0, Math.min(1, a));
        const out = composeColor(picker.value, alpha);
        text.value = out;
        this.setVar(v, out);
      });

      row.append(picker, label, text, alphaEl);
      this.rowsEl.appendChild(row);
    }
  }
}
